/** Time and number formatting for the v2 screens: always Amsterdam time, Dutch notation. */

const ZONE = 'Europe/Amsterdam';

const clockFormat = new Intl.DateTimeFormat('nl-NL', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' });
const clockSecondsFormat = new Intl.DateTimeFormat('nl-NL', { timeZone: ZONE, hour: '2-digit', minute: '2-digit', second: '2-digit' });
const dayFormat = new Intl.DateTimeFormat('nl-NL', { timeZone: ZONE, weekday: 'short', day: 'numeric', month: 'short' });
const keyFormat = new Intl.DateTimeFormat('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' });

const toDate = (value?: string | Date | null): Date | null => {
    if (!value) return null;
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
};

/** 14:20 */
export const clock = (value?: string | Date | null): string => {
    const date = toDate(value);
    return date ? clockFormat.format(date) : '';
};

/** 14:20:05 */
export const clockSeconds = (value?: string | Date | null): string => {
    const date = toDate(value);
    return date ? clockSecondsFormat.format(date) : '';
};

/** The Amsterdam calendar day of a moment, as yyyy-mm-dd, to group by. */
export const dayKey = (value?: string | Date | null): string => {
    const date = toDate(value);
    return date ? keyFormat.format(date) : '';
};

/** "Vandaag", "Gisteren" or "di 3 nov." */
export const dayLabel = (value?: string | Date | null, now = new Date()): string => {
    const key = dayKey(value);
    if (!key) return '';
    if (key === dayKey(now)) return 'Vandaag';
    if (key === dayKey(new Date(now.getTime() - 86400000))) return 'Gisteren';
    // Another year says so, or 28 okt 2024 reads as the 28th of this October.
    const date = toDate(value)!;
    return date.getFullYear() === now.getFullYear() ? dayFormat.format(date) : `${dayFormat.format(date)} ${date.getFullYear()}`;
};

/** Today's moments as a clock, older ones with their day. */
export const when = (value?: string | Date | null, now = new Date()): string => {
    const date = toDate(value);
    if (!date) return '';
    return dayKey(date) === dayKey(now) ? clock(date) : `${dayFormat.format(date)} ${clock(date)}`;
};

/** How long ago, short: "8 s", "4 min", "2 u", "3 d". */
export const ago = (value?: string | Date | null, now = Date.now()): string => {
    const date = toDate(value);
    if (!date) return '';
    const seconds = Math.max(0, Math.round((now - date.getTime()) / 1000));
    return duration(seconds);
};

/** How long until, short; "nu" once it has passed. */
export const until = (value?: string | Date | null, now = Date.now()): string => {
    const date = toDate(value);
    if (!date) return '';
    const seconds = Math.round((date.getTime() - now) / 1000);
    return seconds <= 0 ? 'nu' : duration(seconds);
};

export const duration = (seconds: number): string => {
    if (seconds < 60) return `${seconds} s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} u`;
    return `${Math.floor(seconds / 86400)} d`;
};

export const number = (value?: number | null): string =>
    value === undefined || value === null ? '–' : value.toLocaleString('nl-NL');

export const money = (value?: number | null, currency = 'EUR'): string =>
    value === undefined || value === null
        ? '–'
        : value.toLocaleString('nl-NL', { style: 'currency', currency });

/** The build label as people say it: "20261103.2" becomes "1103.2". */
export const shortBuild = (build?: string | null): string => {
    if (!build) return '';
    const match = /20\d{2}(\d{4}\.\d+)/.exec(build);
    if (match) return match[1];
    // A commit hash: the first eight characters are enough to tell builds apart.
    return /^[0-9a-f]{12,}$/i.test(build) ? build.slice(0, 8) : build;
};

const APP_SUFFIXES = ['cbrcv', 'cbsnd', 'exrcv', 'exsnd'];

/**
 * "c2econnectortestcbrcv=c0af…;c2econnectortestcbsnd=c0af…" as one short row per app. A single
 * build without "=" stays one row without an app name.
 */
export const splitBuilds = (builds?: string | null): { app: string; build: string }[] => {
    const parts = (builds ?? '')
        .split(';')
        .map((p) => p.trim())
        .filter(Boolean)
        .map((p) => {
            const at = p.indexOf('=');
            return at < 0 ? { app: '', build: p } : { app: p.slice(0, at), build: p.slice(at + 1) };
        });

    return parts.map(({ app, build }) => ({
        app: APP_SUFFIXES.find((s) => app.toLowerCase().endsWith(s)) ?? app,
        build: shortBuild(build),
    }));
};

/** A countdown to a moment, ticking: "3h 51m", "4m 05s", "45s"; "0s" once it has passed. */
export const countdown = (at: number | undefined, now = Date.now()): string => {
    if (at === undefined) return '';
    const total = Math.max(0, Math.ceil((at - now) / 1000));
    const h = Math.floor(total / 3600);
    const m = Math.floor((total % 3600) / 60);
    const s = total % 60;
    if (h > 0) return `${h}h ${m}m`;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
};
